<!-- 
  EventManagement.vue
  이벤트 목록 관리 페이지
  
  - 이벤트 목록 조회, 검색, 필터링 기능 제공
  - 주최자(organizers) 필터링 및 시각화 기능 포함
  - 이벤트 상태 관리 및 통계 조회 기능 제공
-->
<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <h1 class="fw-bold page-title">
            이벤트 관리
          </h1>
          <span class="pageDesc">이벤트를 관리하고 통계를 조회할 수 있습니다.</span>
        </div>
      </div>
    </div>
    
    <!-- 검색 및 필터 영역 -->
    <div class="card-body px-0 pt-0 pb-2 table-with-search" style="margin-top: 20px">
      <div class="card content-wrap-card col-sm-12 col-xs-12 tableCard p-0">
        <div class="table-filter-container">
          <div class="filter-row">
            <div class="filter-item">
              <label>이벤트 주최자</label>
              <div class="search-input-group">
                <a-input 
                  v-model:value="searchParams.organizer" 
                  placeholder="주최자명을 입력하세요" 
                  class="search-input"
                  @press-enter="searchEvents"
                />
              </div>
            </div>
            <div class="filter-item">
              <label>이벤트 상태</label>
              <a-select 
                v-model:value="searchParams.status" 
                style="width: 100%"
                @change="searchEvents"
              >
                <a-select-option value="">전체</a-select-option>
                <a-select-option value="preparing">준비중</a-select-option>
                <a-select-option value="ongoing">진행중</a-select-option>
                <a-select-option value="completed">종료됨</a-select-option>
              </a-select>
            </div>
            <div class="filter-item">
              <label>이벤트 기간</label>
              <a-range-picker 
                v-model:value="dateRange"
                format="YYYY-MM-DD"
                :placeholder="['시작일', '종료일']"
                style="width: 100%"
                @change="handleDateChange"
              />
            </div>
            <div class="filter-item filter-buttons">
              <label>&nbsp;</label>
              <div class="search-input-group">
                <a-button type="primary" class="yellow-button" @click="searchEvents">검색</a-button>
                <a-button @click="resetSearch">초기화</a-button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 이벤트 목록 테이블 -->
        <div class="table-toolbar">
          <div class="total-count">
            총 <span>{{ totalItems }}</span>건
          </div>
          <div class="table-actions">
            <a-select 
              v-model:value="pageSize" 
              style="width: 140px"
              @change="handlePageSizeChange"
            >
              <a-select-option :value="10">10개씩 보기</a-select-option>
              <a-select-option :value="20">20개씩 보기</a-select-option>
              <a-select-option :value="50">50개씩 보기</a-select-option>
            </a-select>
            <a-button type="primary" class="yellow-button add-btn" @click="navigateToAddEvent">
              이벤트 등록
            </a-button>
          </div>
        </div>
        
        <div class="table-responsive table-wrap">
          <a-table 
            class="ant-table-striped"
            size="small"
            bordered
            :loading="loading"
            :scroll="{ x: '1600px' }"
            :columns="columns"
            :data-source="eventList"
            :pagination="false"
          >
            <template #emptyText>
              <div class="noData">
                <i class="fa-solid fa-exclamation-triangle" style="font-size: 24px; margin-bottom: 10px"/><br>
                <span>등록된 이벤트가 없습니다.</span>
              </div>
            </template>
            
            <!-- 셀 커스텀 렌더링 -->
            <template #bodyCell="{ column, text, record }">
              <!-- 이벤트명 -->
              <template v-if="column.dataIndex === 'eventTitle'">
                <div class="event-title" :title="text">{{ text }}</div>
              </template>
              
              <!-- 이벤트 주최자 -->
              <template v-if="column.dataIndex === 'organizers'">
                <div class="organizers-container">
                  <a-tooltip v-if="record.organizers && record.organizers.length > 0" placement="topLeft">
                    <template #title>
                      <div class="organizer-tooltip">
                        <div v-for="(org, idx) in record.organizers" :key="idx" class="organizer-tooltip-item">
                          <div class="organizer-tooltip-name">{{ org.name }}</div>
                          <div class="organizer-tooltip-details" v-if="org.representative || org.businessNumber">
                            <div v-if="org.representative" class="detail-item">
                              <span class="detail-label">대표자:</span> {{ org.representative }}
                            </div>
                            <div v-if="org.businessNumber" class="detail-item">
                              <span class="detail-label">사업자번호:</span> {{ org.businessNumber }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <div :class="['organizer-item', record.organizers.length > 1 ? 'has-multiple' : '']">
                      {{ record.organizers[0].name }}
                      <span v-if="record.organizers.length > 1" class="organizer-count">
                        외 {{ record.organizers.length - 1 }}곳
                      </span>
                    </div>
                  </a-tooltip>
                  <div v-else class="empty-organizer">미지정</div>
                </div>
              </template>
              
              <!-- 이벤트 ID -->
              <template v-if="column.dataIndex === 'id'">
                <div class="event-id-cell">
                  <span class="event-id-text" :title="text">{{ text }}</span>
                  <a-button 
                    size="small" 
                    type="text" 
                    class="copy-btn"
                    @click="copyEventId(text)"
                    :title="'이벤트 ID 복사'"
                  >
                    <i class="fa fa-copy"></i>
                  </a-button>
                </div>
              </template>
              
              <!-- 참여관리 -->
              <template v-if="column.dataIndex === 'participation'">
                <div class="participation-cell">
                  <a-button 
                    size="small" 
                    type="link"
                    class="participation-link-btn"
                    @click="handleParticipationManagement(record)"
                  >
                    참여관리
                  </a-button>
                </div>
              </template>
              
              <!-- 이벤트 기간 -->
              <template v-if="column.dataIndex === 'eventPeriod'">
                {{ formatDate(record.startDate) }} ~ {{ formatDate(record.endDate) }}
              </template>
              
              <!-- 상태 표시 -->
              <template v-if="column.dataIndex === 'status'">
                <span :class="['status-badge', `status-${record.status}`]">{{ getStatusText(record.status) }}</span>
              </template>

              <!-- 인증서 연동 상태 -->
              <template v-if="column.dataIndex === 'certificateLink'">
                <div class="certificate-link-cell">
                  <a-tooltip v-if="record.id === activeEventId" title="인증서 발급 시 이 이벤트에 자동으로 참여자가 등록됩니다">
                    <span class="certificate-badge active">
                      <i class="fa fa-check-circle"></i> 활성
                    </span>
                  </a-tooltip>
                  <span v-else class="certificate-badge inactive">-</span>
                </div>
              </template>

              <!-- 작업 버튼 -->
              <template v-if="column.dataIndex === 'actions'">
                <div class="action-cell">
                  <a-dropdown>
                    <template #overlay>
                      <a-menu>
                        <a-menu-item key="bulk-status" @click="handleViewBulkStatus(record)">
                          <span class="dropdown-item bulk-status">참여자 생성 현황보기</span>
                        </a-menu-item>
                        <a-menu-item key="view" @click="handleViewStatistics(record)">
                          <span class="dropdown-item view">현황</span>
                        </a-menu-item>
                        <template v-if="record.status !== 'completed'">
                          <a-menu-item key="edit" @click="handleEditEvent(record)">
                            <span class="dropdown-item edit">수정</span>
                          </a-menu-item>
                          <a-menu-item key="delete" @click="handleDeleteEvent(record)">
                            <span class="dropdown-item delete">삭제</span>
                          </a-menu-item>
                        </template>
                      </a-menu>
                    </template>
                    <a-button class="action-btn dropdown-btn" size="small">
                      관리 <i class="fa fa-chevron-down" style="font-size: 10px; margin-left: 2px"></i>
                    </a-button>
                  </a-dropdown>
                </div>
              </template>
            </template>
          </a-table>
        </div>
        
        <!-- 페이지네이션 -->
        <div class="pagination-container">
          <a-pagination
            v-model:current="currentPage"
            :total="totalItems"
            :page-size="pageSize"
            show-size-changer
            show-quick-jumper
            @change="handlePageChange"
          />
        </div>
      </div>
    </div>
    
    <!-- 참여자 생성 현황 드로워 -->
    <bulk-attendee-status-drawer
      :visible="bulkStatusDrawerVisible"
      :event-id="selectedEventId"
      :event-title="selectedEventTitle"
      :api-instance="apiInstance"
      @close="bulkStatusDrawerVisible = false"
    />
    
    <!-- 이벤트 삭제 드로워 -->
    <event-delete-drawer
      :visible="deleteDrawerVisible"
      :event-id="deleteEventId"
      :event-title="deleteEventTitle"
      :attendee-count="deleteEventAttendeeCount"
      :api-instance="apiInstance"
      :user-role="$store.state.permission"
      @close="deleteDrawerVisible = false"
      @delete-success="handleDeleteSuccess"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted, getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router';
import dayjs from 'dayjs';
import eventsApis from './api'; // api.js 모듈 import
import { message } from 'ant-design-vue'; // Ant Design Vue message 컴포넌트 직접 import
import BulkAttendeeStatusDrawer from './components/BulkAttendeeStatusDrawer.vue';
import EventDeleteDrawer from './components/EventDeleteDrawer.vue';

/**
 * 이벤트 관리 페이지 컴포넌트
 * 
 * 이벤트 목록을 조회하고 관리하는 페이지입니다.
 * 검색, 필터링, 정렬 기능을 제공하며 주최자(organizers) 배열을 지원합니다.
 */
export default {
  name: "EventManagement",
  components: {
    BulkAttendeeStatusDrawer,
    EventDeleteDrawer
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalItems = ref(0);
    const dateRange = ref([]);
    const eventList = ref([]);
    
    // 참여자 생성 현황 드로워 관련 상태
    const bulkStatusDrawerVisible = ref(false);
    const selectedEventId = ref('');
    const selectedEventTitle = ref('');
    
    // 이벤트 삭제 드로워 관련 상태
    const deleteDrawerVisible = ref(false);
    const deleteEventId = ref('');
    const deleteEventTitle = ref('');
    const deleteEventAttendeeCount = ref(0);

    // 활성 이벤트 ID (인증서 발급 시 자동 등록 대상)
    const activeEventId = ref(null);
    
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    const apiInstance = ref(null);
    
    if (instance && instance.proxy) {
      apiInstance.value = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
      console.log('API 인스턴스 초기화:', apiInstance.value);
    } else {
      console.error('Current instance not available, API initialization failed');
    }
    
    /**
     * 검색 및 필터링 매개변수
     * status: 이벤트 상태 필터
     * organizer: 주최자 필터
     * startDate/endDate: 이벤트 기간 필터
     */
    const searchParams = reactive({
      status: '',
      startDate: null,
      endDate: null,
      organizer: '' // 주최자 필터링을 위한 필드
    });
    
    /**
     * 테이블 열 정의
     * 각 열에 대한 제목, 데이터 필드, 정렬 함수 등을 정의합니다.
     * organizers 필드는 배열을 처리하기 위한 특별한 정렬 로직을 포함합니다.
     */
    const columns = [
      { title: '번호', dataIndex: 'index', key: 'index', align: 'center', width: 70, sorter: (a, b) => a.index - b.index },
      { title: '이벤트명', dataIndex: 'eventTitle', key: 'eventTitle', width: 200, sorter: (a, b) => a.eventTitle.localeCompare(b.eventTitle) },
      { 
        title: '이벤트 주최자', 
        dataIndex: 'organizers', 
        key: 'organizers', 
        align: 'center', 
        width: 150,
        sorter: (a, b) => {
          // 주최자가 없는 경우 처리
          if (!a.organizers || a.organizers.length === 0) return -1;
          if (!b.organizers || b.organizers.length === 0) return 1;
          
          // 첫 번째 주최자의 이름을 기준으로 정렬
          const aName = a.organizers[0].name || '';
          const bName = b.organizers[0].name || '';
          return aName.localeCompare(bName);
        }
      },
      { 
        title: '이벤트 기간', 
        dataIndex: 'eventPeriod', 
        key: 'eventPeriod', 
        align: 'center', 
        width: 180,
        sorter: (a, b) => {
          // 시작일을 기준으로 정렬
          return dayjs(a.startDate).diff(dayjs(b.startDate));
        }
      },
      { title: '상태', dataIndex: 'status', key: 'status', align: 'center', width: 100, sorter: (a, b) => {
          // eventStatus 값이 없는 경우 대비
          const aStatus = a.status || 'preparing';
          const bStatus = b.status || 'preparing';
          return aStatus.localeCompare(bStatus);
        }
      },
      { title: '인증서 연동', dataIndex: 'certificateLink', key: 'certificateLink', align: 'center', width: 110 },
      { title: '대상 고객', dataIndex: 'targetCustomers', key: 'targetCustomers', align: 'center', width: 120, sorter: (a, b) => a.targetCustomers.localeCompare(b.targetCustomers) },
      { title: '이벤트 ID', dataIndex: 'id', key: 'id', align: 'center', width: 250, fixed: 'right' },
      { title: '참여관리', dataIndex: 'participation', key: 'participation', align: 'center', width: 100, fixed: 'right' },
      { title: '관리', dataIndex: 'actions', key: 'actions', align: 'center', width: 180, fixed: 'right' }
    ];
    
    // 날짜 형식화 함수
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD');
    };
    
    // 상태 텍스트 변환 함수
    const getStatusText = (status) => {
      switch (status) {
        case 'preparing': return '준비중';
        case 'ongoing': return '진행중';
        case 'completed': return '종료됨';
        default: return '-';
      }
    };
    
    /**
     * 참여율에 따른 클래스 반환 함수
     * @param {number} rate 참여율
     * @returns {string} CSS 클래스명
     */
    const getParticipationRateClass = (rate) => {
      if (rate === 0) return '';
      if (rate < 20) return 'low-value';
      if (rate < 40) return 'medium-value';
      return 'high-value';
    };
    
    /**
     * API 응답 데이터를 테이블 형식으로 변환하는 함수
     * API 응답에서 받은 데이터를 테이블에 표시할 형식으로 가공합니다.
     * @param {Object} apiData API 응답 데이터
     * @returns {Array} 테이블 형식으로 가공된 데이터 배열
     */
    const mapApiDataToTableFormat = (apiData) => {
      if (!apiData || !Array.isArray(apiData.items)) return [];
      
      return apiData.items.map((item, index) => {
        // 활성화된 기능 목록 추출
        const features = [];
        if (item.shareEnabled) {
          features.push({ type: 'share', name: '공유하기', active: true });
        }
        if (item.appEnabled) {
          features.push({ type: 'app', name: '앱 설치', active: true });
        }
        if (item.surveyEnabled) {
          features.push({ type: 'survey', name: '설문조사', active: true });
        }
        if (item.reservationEnabled) {
          features.push({ type: 'reservation', name: '예약', active: true });
        }
        
        // 경품 정보 생성
        let prizeInfo = '없음';
        let prizeDetail = '';
        if (item.prizes && item.prizes.length > 0) {
          const totalPrizes = item.prizes.reduce((sum, prize) => sum + prize.quantity, 0);
          const prizeNames = item.prizes.map(prize => prize.name).join(', ');
          prizeInfo = `${prizeNames} (총 ${totalPrizes}개)`;
          
          // 상세 정보는 첫 번째 경품의 설명으로 설정
          if (item.prizes[0].description) {
            prizeDetail = item.prizes[0].description;
          }
        }
        
        return {
          id: item._id || `item-${index}`,
          key: item._id || `key-${index}`,
          index: index + 1, // 1-based index for display
          eventTitle: item.eventTitle || '',
          eventType: item.eventType || '',
          // organizers 필드는 API 응답 구조를 그대로 사용
          organizers: Array.isArray(item.organizers) 
            ? item.organizers.map(org => ({
                id: org.organizerId,
                name: org.organizerName,
                representative: org.representative || '',
                businessNumber: org.organizerCno || ''
              }))
            : [],
          features: features,
          startDate: item.startDate || '',
          endDate: item.endDate || '',
          status: item.eventStatus || 'preparing',
          targetCustomers: item.targetCustomers || '',
          participants: item.participants || 0,
          prizeInfo: prizeInfo,
          prizeDetail: prizeDetail,
          announcementDate: item.announcementDate || '',
          // 통계 데이터가 없는 경우 0으로 설정
          surveyRate: item.surveyRate || 0,
          shareRate: item.shareRate || 0,
          appInstallRate: item.appInstallRate || 0
        };
      });
    };
    
    /**
     * API에서 이벤트 목록을 불러오는 함수
     * 검색 매개변수와 페이지네이션 정보를 기반으로 API 호출을 수행합니다.
     */
    const fetchEvents = async () => {
      loading.value = true;
      
      try {
        // api.js를 사용하여 이벤트 데이터 조회
        if (!apiInstance.value) {
          console.error('API 인스턴스가 초기화되지 않았습니다.');
          if (window.app?.$message) {
            window.app.$message.error('서버 연결에 필요한 인스턴스가 준비되지 않았습니다.');
          }
          loading.value = false;
          return;
        }
        
        // API 호출 매개변수 구성
        const params = {
          page: currentPage.value,
          limit: pageSize.value,
          status: searchParams.status || undefined,
          startDate: searchParams.startDate || undefined,
          endDate: searchParams.endDate || undefined,
          organizer: searchParams.organizer || undefined
        };
        
        // api.js의 getEvents 함수 호출 (콜백 스타일)
        apiInstance.value.getEvents(
          params,
          (response) => {
            console.log('이벤트 목록 로드 성공:', response);
            const apiData = response.data;
            eventList.value = mapApiDataToTableFormat(apiData);
            totalItems.value = apiData.total || 0;
          },
          (error) => {
            console.error('이벤트 목록 로드 중 오류 발생:', error);
            // 에러 메시지 표시
            if (window.app?.$message) {
              window.app.$message.error('이벤트 목록을 불러오는 중 오류가 발생했습니다.');
            }
          }
        );
      } catch (error) {
        console.error('이벤트 목록 로드 중 예외 발생:', error);
        if (window.app?.$message) {
          window.app.$message.error('이벤트 목록을 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        loading.value = false;
      }
    };
    
    // 검색 함수
    const searchEvents = () => {
      currentPage.value = 1; // 검색 시 첫 페이지로 이동
      
      // 날짜 범위 설정
      if (dateRange.value && dateRange.value.length === 2) {
        searchParams.startDate = dateRange.value[0]?.format('YYYY-MM-DD') || null;
        searchParams.endDate = dateRange.value[1]?.format('YYYY-MM-DD') || null;
      } else {
        searchParams.startDate = null;
        searchParams.endDate = null;
      }
      
      // API 호출 함수 실행
      fetchEvents();
    };
    
    // 날짜 변경 핸들러
    const handleDateChange = () => {
      searchEvents();
    };
    
    // 검색 초기화
    const resetSearch = () => {
      searchParams.status = '';
      searchParams.organizer = ''; // 주최자 필터 초기화
      searchParams.startDate = null;
      searchParams.endDate = null;
      dateRange.value = [];
      
      // API 호출 함수 실행
      fetchEvents();
    };
    
    // 페이지 변경 핸들러
    const handlePageChange = (page) => {
      currentPage.value = page;
      fetchEvents();
    };
    
    // 페이지 크기 변경 핸들러
    const handlePageSizeChange = (size) => {
      pageSize.value = size;
      currentPage.value = 1;
      fetchEvents();
    };
    
    // 이벤트 등록 페이지로 이동
    const navigateToAddEvent = () => {
      router.push('/events/create');
    };
    
    // 이벤트 통계 보기
    const handleViewStatistics = (record) => {
      // 통계 페이지로 이동
      router.push(`/events/statistics/${record.id}`);
    };
    
    // 이벤트 수정
    const handleEditEvent = (record) => {
      router.push(`/events/${record.id}/edit`);
    };
    
    // 이벤트 삭제
    const handleDeleteEvent = async (record) => {
      deleteEventId.value = record.id;
      deleteEventTitle.value = record.eventTitle;
      deleteEventAttendeeCount.value = record.participants || 0;
      deleteDrawerVisible.value = true;
      /* 기존 삭제 로직 주석 처리
      try {
        const confirmed = await window.app.$confirm({
          title: '이벤트 삭제',
          content: `"${record.eventTitle}" 이벤트를 삭제하시겠습니까?`,
          okText: '확인',
          cancelText: '취소'
        });
        
        if (confirmed) {
          loading.value = true;
          
          // API 인스턴스를 사용하여 이벤트 삭제
          if (apiInstance.value) {
            apiInstance.value.deleteEvent(
              record.id,
              () => {
                message.success(`${record.eventTitle} 이벤트가 삭제되었습니다.`);
                // 목록 다시 불러오기
                fetchEvents();
              },
              (error) => {
                console.error('이벤트 삭제 중 오류 발생:', error);
                message.error('이벤트 삭제 중 오류가 발생했습니다.');
                loading.value = false;
              }
            );
          } else {
            console.error('API 인스턴스가 초기화되지 않았습니다.');
            message.error('서버 연결에 필요한 인스턴스가 준비되지 않았습니다.');
            loading.value = false;
          }
        }
      } catch (error) {
        console.error('이벤트 삭제 중 오류 발생:', error);
        message.error('이벤트 삭제 중 오류가 발생했습니다.');
        loading.value = false;
      }
      */
    };
    
    // 참여자 생성 현황 보기
    const handleViewBulkStatus = (record) => {
      selectedEventId.value = record.id;
      selectedEventTitle.value = record.eventTitle;
      bulkStatusDrawerVisible.value = true;
    };
    
    // 이벤트 ID 복사
    const copyEventId = async (eventId) => {
      try {
        await navigator.clipboard.writeText(eventId);
        message.success('이벤트 ID가 클립보드에 복사되었습니다.');
      } catch (error) {
        console.error('클립보드 복사 실패:', error);
        message.error('클립보드 복사에 실패했습니다.');
      }
    };
    
    // 참여관리 페이지로 이동
    const handleParticipationManagement = (record) => {
      router.push({
        path: '/events/participation',
        query: { 
          eventId: record.id
        }
      });
    };
    

    
    /**
     * 활성 이벤트 ID 조회
     * 인증서 발급 시 자동 등록되는 이벤트 ID를 조회합니다.
     */
    const fetchActiveEventId = () => {
      if (!apiInstance.value) {
        console.error('API 인스턴스가 초기화되지 않았습니다.');
        return;
      }

      apiInstance.value.getActiveEventId(
        (response) => {
          const configValue = response.data?.configValue;
          activeEventId.value = configValue || null;
          console.log('활성 이벤트 ID 조회 성공:', activeEventId.value);
        },
        (error) => {
          console.error('활성 이벤트 ID 조회 실패:', error);
          activeEventId.value = null;
        }
      );
    };

    /**
     * 삭제 성공 후 처리
     * @param {string} deleteType - 삭제 타입 ('soft' 또는 'hard')
     */
    const handleDeleteSuccess = (deleteType) => {
      // 삭제 타입에 따른 메시지 표시 (선택적)
      if (deleteType === 'hard') {
        console.log('영구 삭제 완료');
      } else {
        console.log('소프트 삭제 완료');
      }
      
      // 목록 새로고침
      fetchEvents();
    };

    /**
     * 컴포넌트 마운트 시 실행되는 함수
     * 초기 데이터를 로드하고 주최자 옵션을 추출합니다.
     */
    onMounted(() => {
      // 초기 데이터 로드
      fetchEvents();
      // 활성 이벤트 ID 조회
      fetchActiveEventId();
    });
    
    return {
      loading,
      currentPage,
      pageSize,
      totalItems,
      searchParams,
      dateRange,
      columns,
      eventList,
      formatDate,
      getStatusText,
      getParticipationRateClass,
      searchEvents,
      handleDateChange,
      resetSearch,
      handlePageChange,
      handlePageSizeChange,
      navigateToAddEvent,
      handleViewStatistics,
      handleEditEvent,
      handleDeleteEvent,
      handleDeleteSuccess,
      handleViewBulkStatus,
      handleParticipationManagement,
      copyEventId,
      bulkStatusDrawerVisible,
      selectedEventId,
      selectedEventTitle,
      deleteDrawerVisible,
      deleteEventId,
      deleteEventTitle,
      deleteEventAttendeeCount,
      apiInstance,
      activeEventId
    };
  }
};
</script>

<style scoped lang="scss">
.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
  margin-top: 8px;
}

.table-filter-container {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.filter-item {
  flex: 1;
  min-width: 200px;
  position: relative;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 14px;
  }
  
  .help-text {
    font-size: 11px;
    color: #888;
    margin-top: 4px;
  }
}

/* Form 컴포넌트 스타일 통일 */
:deep(.ant-select),
:deep(.ant-picker),
:deep(.ant-input) {
  width: 100%;
  min-height: 36px;
  line-height: 1.5;
  font-size: 14px;
}

:deep(.ant-select-selector) {
  height: 36px !important;
  padding: 0 11px !important;
}

:deep(.ant-select-selection-search) {
  height: 34px !important;
  line-height: 34px !important;
}

:deep(.ant-select-selection-item) {
  line-height: 34px !important;
}

:deep(.ant-picker) {
  padding: 4px 11px;
}

.search-input-group {
  display: flex;
  gap: 8px;
  width: 100%;
  
  .search-input {
    flex: 1;
  }
  
  :deep(.ant-btn) {
    height: 36px;
    padding: 4px 15px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
}

.total-count {
  font-size: 14px;
  
  span {
    font-weight: bold;
    color: #1890ff;
  }
}

.table-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.add-btn {
  margin-left: 8px;
}

.table-wrap {
  margin: 0 16px;
  overflow-x: auto;
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.noData {
  text-align: center;
  padding: 24px;
  color: #999;
}

.event-title {
  text-align: left;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
}

.status-preparing {
  background-color: #e0e0e0;
  color: #333;
}

.status-ongoing {
  background-color: #4caf50;
  color: white;
}

.status-completed {
  background-color: #f44336;
  color: white;
}

.action-cell {
  display: flex;
  justify-content: center;
  gap: 3px;
}

.action-btn {
  padding: 0 5px;
  height: 20px;
  font-size: 11px;
  line-height: 1;
  
  &.view-btn {
    background-color: #4caf50;
    border-color: #4caf50;
    color: white;
    
    &:hover {
      background-color: darken(#4caf50, 10%);
      border-color: darken(#4caf50, 10%);
    }
  }
  
  &.send-btn {
    background-color: #9c27b0;
    border-color: #9c27b0;
    color: white;
    
    &:hover {
      background-color: darken(#9c27b0, 10%);
      border-color: darken(#9c27b0, 10%);
    }
  }
  
  &.edit-btn {
    background-color: #2196f3;
    border-color: #2196f3;
    color: white;
    
    &:hover {
      background-color: darken(#2196f3, 10%);
      border-color: darken(#2196f3, 10%);
    }
  }
  
  &.delete-btn {
    background-color: #f44336;
    border-color: #f44336;
    color: white;
    
    &:hover {
      background-color: darken(#f44336, 10%);
      border-color: darken(#f44336, 10%);
    }
  }
}

.stat-value {
  font-weight: bold;
}

.low-value {
  color: #f44336;
}

.medium-value {
  color: #ff9800;
}

.high-value {
  color: #4caf50;
}

.feature-tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  margin-right: 4px;
  text-align: center;
  min-width: 40px;
  
  &.share {
    background-color: #e3f2fd;
    color: #2196f3;
  }
  
  &.survey {
    background-color: #e8f5e9;
    color: #4caf50;
  }
  
  &.app {
    background-color: #fff3e0;
    color: #ff9800;
  }
  
  &.inactive {
    background-color: #f5f5f5;
    color: #aaa;
  }
}

.empty-organizer, .no-prize, .no-features {
  color: #999;
  font-size: 12px;
  font-style: italic;
}

.prize-info-container {
  max-width: 200px;
  text-align: left;
}

.prize-detail {
  font-size: 0.9em;
  color: #666;
  margin-top: 4px;
  white-space: normal;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.organizers-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.organizer-item {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: #f0f7ff;
  color: #1890ff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    background-color: #e6f7ff;
  }
  
  &.has-multiple {
    background-color: #e6f7ff;
    border: 1px dashed #1890ff;
    position: relative;
    
    &:hover {
      background-color: #d6f0ff;
    }
  }
}

.organizer-count {
  font-size: 11px;
  color: #666;
  margin-left: 4px;
  white-space: nowrap;
  font-weight: 500;
  color: #1890ff;
}

/* 툴팁 스타일 */
.organizer-tooltip {
  width: 300px;
  padding: 4px;
}

.organizer-tooltip-item {
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.2);
  
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
}

.organizer-tooltip-name {
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 14px;
}

.organizer-tooltip-details {
  font-size: 12px;
}

.detail-item {
  margin-top: 2px;
}

.detail-label {
  opacity: 0.8;
  margin-right: 4px;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

:deep(.ant-table-tbody > tr > td) {
  transition: none !important;
  padding: 4px 8px !important;
  font-size: 12px !important;
}

:deep(.ant-table-thead > tr > th) {
  font-size: 12px !important;
}

@media screen and (max-width: 767px) {
  .filter-row {
    flex-direction: column;
    gap: 12px;
  }
  
  .filter-item {
    width: 100%;
    min-width: auto;
  }
  
  .table-toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .table-actions {
    width: 100%;
    justify-content: space-between;
  }
  
  .organizer-tooltip {
    width: 250px;
  }
}

@media screen and (min-width: 768px) and (max-width: 1023px) {
  .filter-row {
    flex-wrap: wrap;
  }
  
  .filter-item {
    min-width: calc(50% - 8px);
    flex: 0 0 calc(50% - 8px);
  }
}

/* 일관된 폼 컴포넌트 높이 확보를 위한 추가 스타일 */
:deep(.ant-select-single .ant-select-selector .ant-select-selection-item),
:deep(.ant-select-single .ant-select-selector .ant-select-selection-placeholder) {
  line-height: 34px !important;
}

:deep(.ant-picker-input > input) {
  height: 26px !important;
}

:deep(.ant-input) {
  height: 36px !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

/* 드롭다운 메뉴 스타일 */
:deep(.ant-dropdown-menu) {
  padding: 4px 0;
  min-width: 100px;
}

:deep(.ant-dropdown-menu-item) {
  padding: 4px 12px;
  font-size: 12px;
  
  &:hover {
    background-color: #f5f5f5;
  }
}

.dropdown-item {
  display: block;
  line-height: 1.5;
  
  &.view {
    color: #4caf50;
  }
  
  &.send {
    color: #9c27b0;
  }
  
  &.bulk-status {
    color: #673ab7;
  }
  
  
  &.edit {
    color: #2196f3;
  }
  
  &.delete {
    color: #f44336;
  }
}

.dropdown-btn {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
}

.event-id-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  
  .event-id-text {
    font-family: monospace;
    font-size: 11px;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .copy-btn {
    padding: 2px 6px;
    min-width: auto;
    height: auto;
    line-height: 1;
    
    i {
      font-size: 12px;
      color: #666;
    }
    
    &:hover {
      background-color: #f0f0f0;
      
      i {
        color: #1890ff;
      }
    }
  }
}

.participation-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.participation-link-btn {
  color: #1890ff !important;
  font-size: 13px;
  font-weight: 500;
  padding: 0 4px;
  height: auto;
  text-decoration: underline;

  span {
    color: #1890ff !important;
    font-weight: 500;
  }

  &:hover:not(:disabled) {
    color: #40a9ff !important;

    span {
      color: #40a9ff !important;
    }
  }

  &:disabled {
    color: rgba(0, 0, 0, 0.25) !important;
    cursor: not-allowed !important;
    text-decoration: none;

    span {
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }
}

/* 인증서 연동 배지 스타일 */
.certificate-link-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.certificate-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;

  &.active {
    background-color: #e6f7e6;
    color: #389e0d;
    border: 1px solid #b7eb8f;

    i {
      margin-right: 4px;
      font-size: 10px;
    }
  }

  &.inactive {
    color: #999;
    font-size: 12px;
  }
}
</style>
